import { useState, useMemo, useEffect, useRef } from 'react'
import { Map, MapControls, MapMarker, MarkerContent, MarkerTooltip, useMap } from '@/components/ui/map'
import { sanitizeMapPopupHtml } from '../../utils/sanitizeHtml'
import { filterVehicleGroupKeys, isEntityUlid } from '../../utils/vehicleIdStandardise'
import MapLibreGL from 'maplibre-gl'

/** Trip-level info (from Python route map / trip CSV). */
interface TripInfo {
  trip_start_time?: string
  trip_end_time?: string
  mileage_miles?: number
  mileage_km?: number
  trip_distance?: number
  trip_miles?: number
  emissions_kg?: number
  co2e_per_mile?: number
  trip_duration_minutes?: number
  trip_duration?: number
  [key: string]: unknown
}

/** Route entry: segments + optional emissions and trip-level data (from Python with utilisation/trip CSV). */
interface RouteEntry {
  segments: Array<Array<[number, number]>>
  count: number
  type?: string
  emissions_kg?: number | null
  miles?: number | null
  co2e_per_mile?: number | null
  trips?: TripInfo[]
  trip_segments?: Array<{ coords: Array<[number, number]>; trip_index?: number; emissions_kg?: number; miles?: number; co2e_per_mile?: number; trip_info?: TripInfo }>
}

/** Route data from backend. With utilisation/trip CSV, group_data includes emissions and trips (same as HTML route map). */
interface RouteData {
  center: [number, number]
  zoom: number
  station_markers: Array<{ lat: number; lon: number; name: string; popup: string }>
  group_data: {
    Vehicle: { [id: string]: RouteEntry }
    Type: { [type: string]: RouteEntry }
  }
}

interface GeographicRouteMapProps {
  data: RouteData
  /** When true (demo users), hide real depot/customer names from map popups. */
  redact?: boolean
  /** When true, render only controls + layers (no Map); parent must render a single shared Map. */
  embedInMap?: boolean
  /** When Group By is Vehicle Reg, use this to show customerVehicleName in the Value dropdown */
  getVehicleDisplayName?: (vehicleId: string) => string
  /** When set, show only route segments for dates in this month (uses YYYY-MM-DD keys in data). */
  selectedMonth?: { year: number; month: number } | null
  /** When set, show only route segments for this single date (YYYY-MM-DD). Takes precedence over selectedMonth. */
  selectedDate?: string | null
  /** Dates to show in the Date dropdown (YYYY-MM-DD). When length > 0, Date dropdown is shown. */
  availableDates?: string[]
  /** Called when user selects a date from the Date dropdown. */
  onSelectedDateChange?: (date: string | null) => void
  /** Controlled: Group By (Vehicle/Type). When provided, selection stays in sync across map tabs. */
  groupBy?: 'Vehicle' | 'Type'
  /** Controlled: selected value in Group By dropdown. */
  selectedGroupValue?: string | null
  /** Controlled: Show All checkbox. */
  showAll?: boolean
  onGroupByChange?: (v: 'Vehicle' | 'Type') => void
  onSelectedGroupValueChange?: (v: string | null) => void
  onShowAllChange?: (v: boolean) => void
}

const MIN_POINTS = 2

type TripSortKey = 'trip_number' | 'carbon_intensity' | 'mileage' | 'duration'
type TripSortOrder = 'asc' | 'desc'

function getTripMileage(trip: TripInfo): number | null {
  const distMi = trip.mileage_miles ?? (trip.mileage_km != null ? (trip.mileage_km as number) / 1.60934 : null) ?? trip.trip_miles ?? trip.trip_distance
  return distMi != null ? Number(distMi) : null
}

function getTripDurationMinutes(trip: TripInfo): number | null {
  const d = trip.trip_duration_minutes ?? trip.trip_duration
  return d != null ? Number(d) : null
}

function getTripCo2ePerMile(trip: TripInfo): number | null {
  const dist = getTripMileage(trip)
  const emissions = trip.emissions_kg != null ? Number(trip.emissions_kg) : null
  const co2e = trip.co2e_per_mile != null ? Number(trip.co2e_per_mile) : (emissions != null && dist != null && dist > 0 ? emissions / dist : null)
  return co2e != null && co2e > 0 ? co2e : null
}

/** Segment with optional intensity for colouring (kg CO₂e/mile). */
type SegmentWithIntensity = { seg: Array<[number, number]>; co2e_per_mile?: number | null }

/** Colour scale: green (low) -> yellow -> red (high). No data = gray. */
function lineColorExpression(): MapLibreGL.ExpressionSpecification {
  return [
    'case',
    ['all', ['has', 'co2e_per_mile'], ['!=', ['get', 'co2e_per_mile'], null]],
    [
      'interpolate',
      ['linear'],
      ['get', 'co2e_per_mile'],
      0, '#22c55e',
      0.25, '#4ade80',
      0.5, '#eab308',
      0.75, '#f97316',
      1.0, '#dc2626',
      1.5, '#b91c1c',
    ],
    '#737373', // no data or null
  ]
}

/** Single colour for a given intensity (same scale as route lines). */
function getColorForIntensity(co2e_per_mile: number | null): string {
  if (co2e_per_mile == null) return '#737373'
  if (co2e_per_mile <= 0.25) return '#4ade80'
  if (co2e_per_mile <= 0.5) return '#eab308'
  if (co2e_per_mile <= 0.75) return '#f97316'
  if (co2e_per_mile <= 1) return '#dc2626'
  return '#b91c1c'
}

function RouteLayer({
  segmentsWithIntensity,
  theme: _theme,
}: {
  segmentsWithIntensity: SegmentWithIntensity[]
  theme: 'light' | 'dark'
}) {
  const { map, isLoaded } = useMap()
  const sourceId = 'route-source'
  const lineLayerId = 'route-lines'

  useEffect(() => {
    if (!isLoaded || !map || segmentsWithIntensity.length === 0) return
    const features = segmentsWithIntensity
      .filter((s) => s.seg.length >= 2)
      .map(({ seg, co2e_per_mile }) => ({
        type: 'Feature' as const,
        properties: { co2e_per_mile: co2e_per_mile ?? null },
        geometry: {
          type: 'LineString' as const,
          coordinates: seg.map(([lat, lon]) => [lon, lat] as [number, number]),
        },
      }))
    const geojson = { type: 'FeatureCollection' as const, features }
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: geojson })
    } else {
      (map.getSource(sourceId) as MapLibreGL.GeoJSONSource).setData(geojson)
    }
    if (!map.getLayer(lineLayerId)) {
      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': lineColorExpression(),
          'line-width': 3,
          'line-opacity': 0.9,
        },
      })
    }
    return () => {
      try {
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch {
        /* ignore */
      }
    }
  }, [isLoaded, map, segmentsWithIntensity, sourceId, lineLayerId])

  return null
}

/** Highlight layer for selected trip only: same intensity colour as route, with subtle opacity pulse. */
function RouteHighlightLayer({
  segments,
  co2e_per_mile,
}: {
  segments: Array<Array<[number, number]>>
  co2e_per_mile: number | null
}) {
  const { map, isLoaded } = useMap()
  const sourceId = 'route-highlight-source'
  const lineLayerId = 'route-highlight-lines'
  const lineColor = getColorForIntensity(co2e_per_mile)

  useEffect(() => {
    if (!isLoaded || !map || segments.length === 0) return
    const features = segments
      .filter((seg) => seg.length >= 2)
      .map((seg) => ({
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: seg.map(([lat, lon]) => [lon, lat] as [number, number]),
        },
      }))
    const geojson = { type: 'FeatureCollection' as const, features }
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: geojson })
    } else {
      (map.getSource(sourceId) as MapLibreGL.GeoJSONSource).setData(geojson)
    }
    if (!map.getLayer(lineLayerId)) {
      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': lineColor,
          'line-width': 5,
          'line-opacity': 0.9,
        },
      })
    }
    return () => {
      try {
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch {
        /* ignore */
      }
    }
  }, [isLoaded, map, segments, sourceId, lineLayerId, lineColor])

  useEffect(() => {
    if (!isLoaded || !map || !map.getLayer(lineLayerId)) return
    map.setPaintProperty(lineLayerId, 'line-color', lineColor)
  }, [isLoaded, map, lineLayerId, lineColor])

  // Obvious pulse: opacity 0.5–1 and line-width 4–7
  useEffect(() => {
    if (!isLoaded || !map || segments.length === 0 || !map.getLayer(lineLayerId)) return
    let rafId = 0
    const duration = 600
    const tick = (t: number) => {
      const cycle = (t % (duration * 2)) / duration
      const opacity = cycle < 1 ? 0.5 + 0.5 * cycle : 1 - 0.5 * (cycle - 1)
      const width = cycle < 1 ? 4 + 3 * cycle : 7 - 3 * (cycle - 1)
      try {
        if (map.getLayer(lineLayerId)) {
          map.setPaintProperty(lineLayerId, 'line-opacity', opacity)
          map.setPaintProperty(lineLayerId, 'line-width', width)
        }
      } catch {
        /* ignore */
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [isLoaded, map, segments.length, lineLayerId])

  return null
}

/** Invisible hit-target layer over trip segments; click selects that trip (same as right panel). */
function RouteClickLayer({
  tripSegments,
  onTripSelect,
}: {
  tripSegments: Array<{ coords: Array<[number, number]> }>
  onTripSelect: (tripIndex: number) => void
}) {
  const { map, isLoaded } = useMap()
  const sourceId = 'route-click-source'
  const lineLayerId = 'route-click-lines'

  useEffect(() => {
    if (!isLoaded || !map || tripSegments.length === 0) return
    const features = tripSegments
      .map((seg, tripIndex) => {
        const coords = seg.coords
        if (!coords || coords.length < 2) return null
        return {
          type: 'Feature' as const,
          properties: { trip_index: tripIndex },
          geometry: {
            type: 'LineString' as const,
            coordinates: coords.map(([lat, lon]) => [lon, lat] as [number, number]),
          },
        }
      })
      .filter((f): f is NonNullable<typeof f> => f != null)
    if (features.length === 0) return
    const geojson = { type: 'FeatureCollection' as const, features }
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: geojson })
    } else {
      (map.getSource(sourceId) as MapLibreGL.GeoJSONSource).setData(geojson)
    }
    if (!map.getLayer(lineLayerId)) {
      map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: sourceId,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#888',
          'line-width': 14,
          'line-opacity': 0.01,
        },
      })
    }
    return () => {
      try {
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch {
        /* ignore */
      }
    }
  }, [isLoaded, map, tripSegments, sourceId, lineLayerId])

  const onTripSelectRef = useRef(onTripSelect)
  onTripSelectRef.current = onTripSelect
  useEffect(() => {
    if (!isLoaded || !map || tripSegments.length === 0) return
    const handleClick = (e: MapLibreGL.MapLayerMouseEvent) => {
      const f = e.features?.[0]
      const idx = f?.properties?.trip_index
      if (typeof idx === 'number' && idx >= 0 && idx < tripSegments.length) {
        onTripSelectRef.current(idx)
      }
    }
    map.on('click', lineLayerId, handleClick)
    const setPointer = () => { map.getCanvas().style.cursor = 'pointer' }
    const resetCursor = () => { map.getCanvas().style.cursor = '' }
    map.on('mouseenter', lineLayerId, setPointer)
    map.on('mouseleave', lineLayerId, resetCursor)
    return () => {
      map.off('click', lineLayerId, handleClick)
      map.off('mouseenter', lineLayerId, setPointer)
      map.off('mouseleave', lineLayerId, resetCursor)
      map.getCanvas().style.cursor = ''
    }
  }, [isLoaded, map, lineLayerId, tripSegments.length])

  return null
}

// Compute bounds from segments without spreading (avoids "Maximum call stack size exceeded" for Show All)
const MAX_POINTS_FOR_BOUNDS = 50000
function boundsFromSegments(segments: Array<Array<[number, number]>>): { minLat: number; maxLat: number; minLon: number; maxLon: number } | null {
  if (segments.length === 0) return null
  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity
  let count = 0
  for (const seg of segments) {
    if (!Array.isArray(seg)) continue
    for (const pt of seg) {
      if (!Array.isArray(pt) || pt.length < 2) continue
      if (count++ >= MAX_POINTS_FOR_BOUNDS) break
      const [lat, lon] = pt
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      if (lon < minLon) minLon = lon
      if (lon > maxLon) maxLon = lon
    }
    if (count >= MAX_POINTS_FOR_BOUNDS) break
  }
  if (minLat === Infinity) return null
  return { minLat, maxLat, minLon, maxLon }
}

function AutoFitRoutes({
  segments,
  paddingRight = 50,
}: {
  segments: Array<Array<[number, number]>>
  paddingRight?: number
}) {
  const { map, isLoaded } = useMap()
  useEffect(() => {
    if (!isLoaded || !map || segments.length === 0) return
    const b = boundsFromSegments(segments)
    if (!b) return
    const { minLat, maxLat, minLon, maxLon } = b
    const pad = 0.1
    const latPad = (maxLat - minLat) * pad || 0.01
    const lonPad = (maxLon - minLon) * pad || 0.01
    map.fitBounds(
      [
        [minLon - lonPad, minLat - latPad],
        [maxLon + lonPad, maxLat + latPad],
      ],
      { padding: { top: 50, bottom: 50, left: 50, right: paddingRight }, duration: 500 }
    )
  }, [isLoaded, map, segments, paddingRight])
  return null
}

/** Merge multiple RouteEntry into one (concat segments, sum count). */
function mergeRouteEntries(entries: RouteEntry[]): RouteEntry {
  const segments = entries.flatMap((e) => e.segments || [])
  const count = entries.reduce((s, e) => s + (e.count ?? 0), 0)
  const trips = entries.flatMap((e) => e.trips || [])
  const trip_segments = entries.flatMap((e) => e.trip_segments || [])
  return { segments, count, trips: trips.length ? trips : undefined, trip_segments: trip_segments.length ? trip_segments : undefined }
}

// Normalize group_data so backend keys "vehicle"/"type" work as "Vehicle"/"Type"
// and date-nested entries (from supatrak_geographic_mapping.py) are flattened: each vehicle/type
// may be { "all": RouteEntry, "YYYY-MM-DD": RouteEntry }; when selectedDate set use that key; when selectedMonth set use only that month's date keys.
function normalizeGroupData(
  groupData: RouteData['group_data'],
  selectedMonth?: { year: number; month: number } | null,
  selectedDate?: string | null
): RouteData['group_data'] {
  if (!groupData || typeof groupData !== 'object') return { Vehicle: {}, Type: {} }
  const raw = groupData as Record<string, Record<string, RouteEntry | { all?: RouteEntry; [date: string]: RouteEntry | undefined }>>

  const resolveEntry = (entry: unknown): RouteEntry | undefined => {
    if (!entry || typeof entry !== 'object') return undefined
    const o = entry as Record<string, unknown>
    if (Array.isArray(o.segments) && typeof o.count === 'number') return entry as RouteEntry
    if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      const val = o[selectedDate]
      if (val && typeof val === 'object' && Array.isArray((val as RouteEntry).segments) && typeof (val as RouteEntry).count === 'number')
        return val as RouteEntry
      // No per-day route bucket: fall through to month merge / "all"
    }
    // Date-nested: optionally filter by selectedMonth
    if (selectedMonth && selectedMonth.month >= 1 && selectedMonth.month <= 12) {
      const prefix = `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}-`
      const monthEntries: RouteEntry[] = []
      for (const key of Object.keys(o)) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(key) && key.startsWith(prefix)) {
          const val = o[key]
          if (val && typeof val === 'object' && Array.isArray((val as RouteEntry).segments) && typeof (val as RouteEntry).count === 'number')
            monthEntries.push(val as RouteEntry)
        }
      }
      if (monthEntries.length > 0) return mergeRouteEntries(monthEntries)
    }
    if (o.all && typeof o.all === 'object' && o.all !== null && Array.isArray((o.all as RouteEntry).segments))
      return o.all as RouteEntry
    return undefined
  }

  const flatten = (dict: Record<string, unknown> | undefined): Record<string, RouteEntry> => {
    if (!dict || typeof dict !== 'object') return {}
    const out: Record<string, RouteEntry> = {}
    for (const [k, v] of Object.entries(dict)) {
      const resolved = resolveEntry(v)
      if (resolved) out[k] = resolved
    }
    return out
  }

  return {
    Vehicle: flatten(raw.Vehicle ?? raw.vehicle),
    Type: flatten(raw.Type ?? raw.type),
  }
}

export function GeographicRouteMap({ data, redact: _redact = false, embedInMap = false, getVehicleDisplayName, selectedMonth = null, selectedDate = null, availableDates = [], onSelectedDateChange, groupBy: groupByProp, selectedGroupValue: selectedGroupValueProp, showAll: showAllProp, onGroupByChange, onSelectedGroupValueChange, onShowAllChange }: GeographicRouteMapProps) {
  const [groupByInternal, setGroupByInternal] = useState<'Vehicle' | 'Type'>('Vehicle')
  const [selectedGroupValueInternal, setSelectedGroupValueInternal] = useState<string | null>(null)
  const [showAllInternal, setShowAllInternal] = useState(false)
  const groupBy = groupByProp ?? groupByInternal
  const setGroupBy = onGroupByChange ? (v: 'Vehicle' | 'Type') => { setGroupByInternal(v); onGroupByChange(v) } : setGroupByInternal
  const selectedGroupValue = selectedGroupValueProp !== undefined ? selectedGroupValueProp : selectedGroupValueInternal
  const setSelectedGroupValue = onSelectedGroupValueChange ? (v: string | null) => { setSelectedGroupValueInternal(v); onSelectedGroupValueChange(v) } : setSelectedGroupValueInternal
  const showAll = showAllProp !== undefined ? showAllProp : showAllInternal
  const setShowAll = onShowAllChange ? (v: boolean) => { setShowAllInternal(v); onShowAllChange(v) } : setShowAllInternal
  const [selectedTripIndex, setSelectedTripIndex] = useState<number | null>(null)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)
  const [tripSortKey, setTripSortKey] = useState<TripSortKey>('trip_number')
  const [tripSortOrder, setTripSortOrder] = useState<TripSortOrder>('asc')
  const mapTheme: 'light' | 'dark' = 'dark'

  // Reset trip selection when route or view changes
  useEffect(() => {
    setSelectedTripIndex(null)
  }, [selectedGroupValue, showAll])

  const groupDataNormalized = useMemo(() => normalizeGroupData(data?.group_data, selectedMonth, selectedDate), [data?.group_data, selectedMonth, selectedDate])
  const groupData = groupDataNormalized?.[groupBy] ?? {}
  const availableGroupValues = useMemo(
    () => filterVehicleGroupKeys(Object.keys(groupData).sort(), groupBy),
    [groupData, groupBy],
  )

  useEffect(() => {
    if (!showAll && availableGroupValues.length > 0) {
      if (!selectedGroupValue || !availableGroupValues.includes(selectedGroupValue)) {
        setSelectedGroupValue(availableGroupValues[0])
      }
    } else if (availableGroupValues.length === 0) setSelectedGroupValue(null)
  }, [availableGroupValues, showAll, selectedGroupValue])

  const routeSegments = useMemo(() => {
    if (!groupData || typeof groupData !== 'object') return []
    if (showAll) {
      const all = Object.entries(groupData)
        .filter(([key]) => groupBy !== 'Vehicle' || !isEntityUlid(key))
        .map(([, r]) => r)
        .filter((r) => r && r.segments && r.count >= MIN_POINTS)
        .flatMap((r) => (r.segments || []).filter((seg): seg is Array<[number, number]> => Array.isArray(seg) && seg.length >= 2))
      return all
    }
    if (!selectedGroupValue) return []
    const r = groupData[selectedGroupValue]
    if (!r || r.count < MIN_POINTS) return []
    return (r.segments || []).filter((seg): seg is Array<[number, number]> => Array.isArray(seg) && seg.length >= 2)
  }, [groupData, groupBy, showAll, selectedGroupValue])

  // When redaction is enabled (demo users), avoid exposing real depot/customer names in station popups.
  const stationMarkers = useMemo(() => {
    const markers = Array.isArray(data.station_markers) ? data.station_markers : []
    if (!_redact) return markers
    return markers.map((marker, idx) => ({
      ...marker,
      popup: `Station ${String.fromCharCode(65 + (idx % 26))}`,
    }))
  }, [data.station_markers, _redact])

  /** Segments with intensity for colouring (green = low CO₂e/mile, red = high). */
  const segmentsWithIntensity = useMemo((): SegmentWithIntensity[] => {
    if (!groupData || typeof groupData !== 'object') return []
    if (showAll) {
      return Object.values(groupData)
        .filter((r) => r && r.segments && r.count >= MIN_POINTS)
        .flatMap((r) =>
          (r.segments || [])
            .filter((seg): seg is Array<[number, number]> => Array.isArray(seg) && seg.length >= 2)
            .map((seg) => ({ seg, co2e_per_mile: r.co2e_per_mile ?? null }))
        )
    }
    if (!selectedGroupValue) return []
    const r = groupData[selectedGroupValue]
    if (!r || r.count < MIN_POINTS || !r.segments) return []
    const intensity = r.co2e_per_mile ?? null
    return (r.segments || [])
      .filter((seg): seg is Array<[number, number]> => Array.isArray(seg) && seg.length >= 2)
      .map((seg) => ({ seg, co2e_per_mile: intensity }))
  }, [groupData, showAll, selectedGroupValue])

  const totalPoints = useMemo(
    () => routeSegments.reduce((sum, seg) => sum + seg.length, 0),
    [routeSegments]
  )

  const selectedRoute = useMemo((): RouteEntry | null => {
    if (showAll || !selectedGroupValue) return null
    const r = groupData[selectedGroupValue]
    return r && r.segments ? (r as RouteEntry) : null
  }, [groupData, showAll, selectedGroupValue])

  /** When user selects a trip, that trip’s coords for highlight + zoom; empty when no trip selected (so we don’t draw full route as highlight). */
  const highlightAndFitSegments = useMemo(() => {
    if (showAll || !selectedGroupValue || selectedTripIndex == null) return []
    const r = groupData[selectedGroupValue] as RouteEntry | undefined
    if (!r?.segments) return []
    const tripSeg = r.trip_segments?.[selectedTripIndex]
    if (tripSeg && tripSeg.coords && tripSeg.coords.length >= 2) {
      return [tripSeg.coords]
    }
    return []
  }, [groupData, showAll, selectedGroupValue, selectedTripIndex])

  /** Intensity for the currently selected trip (for highlight colour). */
  const selectedTripCo2e = useMemo((): number | null => {
    if (showAll || !selectedGroupValue || selectedTripIndex == null) return null
    const r = groupData[selectedGroupValue] as RouteEntry | undefined
    const trip = r?.trips?.[selectedTripIndex]
    return trip ? getTripCo2ePerMile(trip) : (r?.trip_segments?.[selectedTripIndex]?.co2e_per_mile ?? null)
  }, [groupData, showAll, selectedGroupValue, selectedTripIndex])

  /** Trips sorted by current sort key/order, with original index for selection and trip_segments. */
  const sortedTripsWithIndex = useMemo((): Array<{ trip: TripInfo; originalIndex: number }> => {
    const trips = selectedRoute?.trips
    if (!trips?.length) return []
    const withIndex = trips.map((trip, originalIndex) => ({ trip, originalIndex }))
    const sign = tripSortOrder === 'asc' ? 1 : -1
    const cmp = (a: { trip: TripInfo; originalIndex: number }, b: { trip: TripInfo; originalIndex: number }) => {
      switch (tripSortKey) {
        case 'trip_number':
          return sign * (a.originalIndex - b.originalIndex)
        case 'carbon_intensity': {
          const va = getTripCo2ePerMile(a.trip) ?? Infinity
          const vb = getTripCo2ePerMile(b.trip) ?? Infinity
          return sign * (va - vb)
        }
        case 'mileage': {
          const va = getTripMileage(a.trip) ?? Infinity
          const vb = getTripMileage(b.trip) ?? Infinity
          return sign * (va - vb)
        }
        case 'duration': {
          const va = getTripDurationMinutes(a.trip) ?? Infinity
          const vb = getTripDurationMinutes(b.trip) ?? Infinity
          return sign * (va - vb)
        }
      }
    }
    return [...withIndex].sort(cmp)
  }, [selectedRoute?.trips, tripSortKey, tripSortOrder])

  const setGroupByAndFirst = (newGroupBy: 'Vehicle' | 'Type') => {
    setGroupBy(newGroupBy)
    const keys = Object.keys(groupDataNormalized[newGroupBy] ?? {}).sort()
    setSelectedGroupValue(keys.length > 0 ? keys[0] : null)
  }

  const showRightPanel = !showAll && selectedRoute && (selectedRoute.trips?.length || selectedRoute.emissions_kg != null || selectedRoute.miles != null || selectedRoute.co2e_per_mile != null)
  const rightPanelWidth = rightPanelCollapsed ? 48 : 280
  const rightPanelPadding = showRightPanel ? rightPanelWidth + 16 : 50

  const tripListRef = useRef<HTMLDivElement>(null)

  // When a trip is selected (e.g. by clicking the map), expand the right panel and scroll it into view
  useEffect(() => {
    if (selectedTripIndex == null) return
    setRightPanelCollapsed(false)
  }, [selectedTripIndex])

  useEffect(() => {
    if (selectedTripIndex == null || !tripListRef.current) return
    const el = tripListRef.current.querySelector(`[data-trip-index="${selectedTripIndex}"]`)
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100)
      return () => clearTimeout(t)
    }
  }, [selectedTripIndex])

  return (
    <div className="relative w-full h-full pointer-events-none">
      {/* Left: controls only */}
      <div className="absolute top-4 left-4 z-10 bg-card p-4 rounded-lg shadow-lg border border-border min-w-55 pointer-events-auto">
        <h4 className="font-bold mb-3 text-white text-center">Route Map</h4>
        <div className="space-y-3">
          {availableDates.length > 0 && onSelectedDateChange && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Date:</label>
              <select
                value={selectedDate ?? ''}
                onChange={(e) => onSelectedDateChange(e.target.value || null)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="">All dates in period</option>
                {availableDates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Group By:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupByAndFirst(e.target.value as 'Vehicle' | 'Type')}
              className="w-full p-2 border border-border rounded bg-input text-white text-sm"
            >
              <option value="Vehicle">Vehicle Reg</option>
              <option value="Type">Vehicle Type</option>
            </select>
          </div>
          {!showAll && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Value:</label>
              <select
                value={selectedGroupValue || ''}
                onChange={(e) => setSelectedGroupValue(e.target.value || null)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="">Select...</option>
                {availableGroupValues.map((k) => (
                  <option key={k} value={k}>{groupBy === 'Vehicle' && getVehicleDisplayName ? getVehicleDisplayName(k) : k}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="flex items-center text-sm text-foreground">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="mr-2" />
              Show All
            </label>
          </div>
          {totalPoints > 0 && (
            <div className="text-xs pt-2 border-t border-border text-muted-foreground">
              Points: {totalPoints.toLocaleString()}
            </div>
          )}
          {segmentsWithIntensity.length > 0 && (
            <div className="text-xs pt-2 border-t border-border text-muted-foreground">
              <div className="font-medium text-foreground mb-1">Emissions intensity</div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="w-3 h-1 rounded bg-success" title="Low" />
                <span className="text-xs">Low</span>
                <span className="w-3 h-1 rounded bg-warning" />
                <span className="text-xs">Med</span>
                <span className="w-3 h-1 rounded bg-destructive" title="High" />
                <span className="text-xs">High</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: Trip summary (collapsible); click a trip to zoom and highlight it */}
      {showRightPanel && selectedRoute && (
        <div
          className="absolute top-4 right-20 z-10 bg-card rounded-lg shadow-lg border border-border flex flex-col pointer-events-auto overflow-hidden"
          style={{ width: rightPanelCollapsed ? 48 : 280, maxHeight: 'calc(100vh - 8rem)' }}
        >
          <button
            type="button"
            onClick={() => setRightPanelCollapsed((c) => !c)}
            className="flex items-center justify-between w-full p-3 text-left hover:bg-secondary/50 transition-colors"
          >
            <h4 className="font-bold text-white truncate">
              {rightPanelCollapsed ? 'Trips' : 'Trip summary'}
            </h4>
            <span className="text-muted-foreground flex-shrink-0 ml-2" aria-hidden title={rightPanelCollapsed ? 'Expand panel' : 'Collapse panel'}>
              {rightPanelCollapsed ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              )}
            </span>
          </button>
          {!rightPanelCollapsed && (
            <div className="p-4 pt-0 flex flex-col flex-1 min-h-0">
              {selectedRoute.emissions_kg != null || selectedRoute.miles != null || selectedRoute.co2e_per_mile != null ? (
                <div className="text-xs pb-2 border-b border-border text-muted-foreground space-y-1 mb-2">
                  {selectedRoute.emissions_kg != null && (
                    <div>Total emissions: {Number(selectedRoute.emissions_kg).toFixed(2)} kg CO₂e</div>
                  )}
                  {selectedRoute.miles != null && (
                    <div>Miles: {Number(selectedRoute.miles).toFixed(1)}</div>
                  )}
                  {selectedRoute.co2e_per_mile != null && selectedRoute.co2e_per_mile > 0 && (
                    <div>Intensity: {Number(selectedRoute.co2e_per_mile).toFixed(3)} kg CO₂e/mile</div>
                  )}
                </div>
              ) : null}
              {selectedRoute.trips && selectedRoute.trips.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">Sort by</label>
                    <select
                      value={tripSortKey}
                      onChange={(e) => setTripSortKey(e.target.value as TripSortKey)}
                      className="flex-1 min-w-0 p-1.5 text-xs border border-border rounded bg-input text-white"
                    >
                      <option value="trip_number">Trip number</option>
                      <option value="carbon_intensity">Carbon intensity</option>
                      <option value="mileage">Mileage</option>
                      <option value="duration">Duration</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setTripSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                      className="p-1.5 text-xs border border-border rounded bg-input text-white hover:bg-accent"
                      title={tripSortOrder === 'asc' ? 'Ascending – click for descending' : 'Descending – click for ascending'}
                    >
                      {tripSortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                  <div ref={tripListRef} className="text-xs text-muted-foreground space-y-2 overflow-y-auto flex-1 min-h-0">
                    {sortedTripsWithIndex.map(({ trip, originalIndex }) => {
                      const startTime = trip.trip_start_time ? new Date(trip.trip_start_time).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : null
                      const dist = getTripMileage(trip)
                      const emissions = trip.emissions_kg != null ? Number(trip.emissions_kg) : null
                      const co2e = getTripCo2ePerMile(trip)
                      const isSelected = selectedTripIndex === originalIndex
                      const canSelectTrip = (selectedRoute?.trip_segments?.[originalIndex]?.coords?.length ?? 0) >= 2
                      return (
                        <button
                          key={originalIndex}
                          type="button"
                          data-trip-index={originalIndex}
                          onClick={() => canSelectTrip && setSelectedTripIndex(isSelected ? null : originalIndex)}
                          className={`w-full text-left pl-2 border-l-2 rounded-r transition-colors ${isSelected ? 'border-destructive bg-destructive/10' : 'border-warning/50 hover:bg-secondary/50'} ${canSelectTrip ? 'cursor-pointer' : 'cursor-default opacity-75'}`}
                        >
                          <div className="font-medium text-foreground">
                            Trip {originalIndex + 1}
                            {isSelected && <span className="text-destructive font-semibold"> (Selected)</span>}
                          </div>
                          {startTime && <div>Start: {startTime}</div>}
                          {dist != null && <div>Distance: {dist.toFixed(1)} miles</div>}
                          {emissions != null && <div>Emissions: {emissions.toFixed(2)} kg CO₂e</div>}
                          {co2e != null && <div>CO₂e/mile: {co2e.toFixed(3)}</div>}
                          {canSelectTrip && !isSelected && (
                            <div className="text-xs text-warning/80 mt-1">Click to zoom to this trip</div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">No trip data for this route.</p>
              )}
            </div>
          )}
        </div>
      )}

      {embedInMap ? (
        segmentsWithIntensity.length > 0 && (
          <>
            <RouteLayer segmentsWithIntensity={segmentsWithIntensity} theme={mapTheme} />
            {highlightAndFitSegments.length > 0 && (
              <>
                <RouteHighlightLayer segments={highlightAndFitSegments} co2e_per_mile={selectedTripCo2e} />
                <AutoFitRoutes segments={highlightAndFitSegments} paddingRight={rightPanelPadding} />
              </>
            )}
            {highlightAndFitSegments.length === 0 && (
              <AutoFitRoutes segments={routeSegments} paddingRight={rightPanelPadding} />
            )}
            {selectedRoute?.trip_segments && selectedRoute.trip_segments.length > 0 && (
              <RouteClickLayer
                tripSegments={selectedRoute.trip_segments}
                onTripSelect={setSelectedTripIndex}
              />
            )}
          </>
        )
      ) : (
        <div className="w-full h-full">
          <Map center={[data.center[1], data.center[0]]} zoom={data.zoom} theme={mapTheme}>
            <MapControls position="bottom-right" showZoom showCompass showLocate showFullscreen />
            {stationMarkers.map((marker, idx) => (
              <MapMarker key={idx} longitude={marker.lon} latitude={marker.lat}>
                <MarkerContent>
                  <div className="relative cursor-pointer">
                    <svg width="36" height="48" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' }}>
                      <path d="M12 0C7.58 0 4 3.58 4 8C4 14 12 24 12 24C12 24 20 14 20 8C20 3.58 16.42 0 12 0Z" fill="#F59E0B" stroke="#FFFFFF" strokeWidth="1.5" />
                      <rect x="7" y="10" width="10" height="8" fill="#FFFFFF" stroke="#F59E0B" strokeWidth="0.5" />
                      <rect x="8" y="12" width="2" height="2" fill="#F59E0B" />
                      <rect x="11" y="12" width="2" height="2" fill="#F59E0B" />
                      <rect x="14" y="12" width="2" height="2" fill="#F59E0B" />
                      <line x1="7" y1="14" x2="17" y2="14" stroke="#F59E0B" strokeWidth="0.5" />
                      <polygon points="9,10 12,7 15,10" fill="#FFFFFF" />
                    </svg>
                  </div>
                </MarkerContent>
                <MarkerTooltip className="!p-0 !m-0 bg-background text-white rounded shadow-lg">
                  <div className="px-2 py-1 text-xs" dangerouslySetInnerHTML={{ __html: sanitizeMapPopupHtml(marker.popup) }} />
                </MarkerTooltip>
              </MapMarker>
            ))}
            {segmentsWithIntensity.length > 0 && (
              <>
                <RouteLayer segmentsWithIntensity={segmentsWithIntensity} theme={mapTheme} />
                {highlightAndFitSegments.length > 0 && (
                  <>
                    <RouteHighlightLayer segments={highlightAndFitSegments} co2e_per_mile={selectedTripCo2e} />
                    <AutoFitRoutes segments={highlightAndFitSegments} paddingRight={rightPanelPadding} />
                  </>
                )}
                {highlightAndFitSegments.length === 0 && (
                  <AutoFitRoutes segments={routeSegments} paddingRight={rightPanelPadding} />
                )}
                {selectedRoute?.trip_segments && selectedRoute.trip_segments.length > 0 && (
                  <RouteClickLayer
                    tripSegments={selectedRoute.trip_segments}
                    onTripSelect={setSelectedTripIndex}
                  />
                )}
              </>
            )}
          </Map>
        </div>
      )}
    </div>
  )
}
